import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Content from "../../layout/content/Content";
import Head from "../../layout/head/Head";
import { Card, Row, Col, Button, Spinner } from "reactstrap";
import {
  Block,
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Icon,
} from "../../components/Component";
import {
  Form,
  Input,
  message,
  Table,
  InputNumber,
  Checkbox,
  Button as AntButton,
} from "antd";
import {
  WarningOutlined,
  PlusOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import Swal from "sweetalert2";
import { indianCurrencyFormatter, indianCurrencyParser } from "../../utils/Utils";
import workOrderApis from "../../api/WorkOrderManagement/WorkOrderApis";
import sequenceApis from "../../api/master/sequence";

const WorkOrderRiskflags: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [form] = Form.useForm();

  const [workOrder, setWorkOrder] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [sequenceAccess, setSequenceAccess] = useState<any>();
  const [riskflassItems, setRiskflassItems] = useState<any[]>([]);
  const [initialItemIds, setInitialItemIds] = useState<Set<number>>(new Set());
  const [initialItems, setInitialItems] = useState<any[]>([]); // Store initial state for comparison
  const validationTimeoutRef = useRef<{ [key: number]: NodeJS.Timeout }>({});

  useEffect(() => {
    fetchSequenceAccess();
    if (id) {
      loadWorkOrderData(id);
    }
  }, [id]);

  const loadWorkOrderData = async (workOrderId: string) => {
    try {
      setLoading(true);
      const response: any = await workOrderApis.getWorkOrderById(workOrderId);

      if (response && response.status && response.data) {
        const apiData = response.data;
        setWorkOrder(apiData);

        // Load existing Risk Flag items from workOrderAdvanceDeductions
        const existingItems =
          apiData.workOrderAdvanceDeductions?.filter(
            (item: any) => item.type === "Risk Flag"
          ) || [];

        if (existingItems.length > 0) {
          // Map API response to form format
          const mappedItems = existingItems.map((item: any, index: number) => ({
            id: item.workOrderAdvanceDeductionId || index + 1,
            workOrderAdvanceDeductionId: item.workOrderAdvanceDeductionId,
            name: item.name || "",
            isChargeable: item.isChargable || false,
            amount: item.totalAmount || 0, // Show totalAmount on initial load
            remarks: item.notes || "",
            totalPendingRecoveredAmount: item.totalPendingRecoveredAmount || 0,
            balanceAmount: item.balanceAmount || 0,
            totalRecoveredAmount: item.totalRecoveredAmount || 0,
            isExisting: true, // Flag to identify existing items
          }));
          setRiskflassItems(mappedItems);
          // Store initial items for comparison
          setInitialItems(JSON.parse(JSON.stringify(mappedItems)));
          // Track initial item IDs to identify deleted items
          const initialIds = new Set<number>(
            existingItems
              .map((item: any) => item.workOrderAdvanceDeductionId)
              .filter((id: any) => id != null)
              .map((id: any) => Number(id))
          );
          setInitialItemIds(initialIds);
        } else {
          // Initialize with one empty row if no existing data
          const emptyItem = {
            id: 1,
            name: "",
            isChargeable: false,
            amount: 0,
            remarks: "",
            isExisting: false,
          };
          setRiskflassItems([emptyItem]);
          setInitialItems([]); // No initial items
        }
      } else {
        message.error("Failed to load work order data");
        navigate(-1);
      }
    } catch (error) {
      console.error("Error loading work order:", error);
      message.error("Failed to load work order data");
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const fetchSequenceAccess = async () => {
    try {
      const response = await sequenceApis.fetchSequence();
      const { data } = response;
      const today = new Date();
      const _sequenceAccess = data?.filter(
        (item: any) =>
          item.moduleName === "Work Order Riskflass" &&
          new Date(item.endDate) > today &&
          item.status === "Active"
      );
      if (_sequenceAccess.length > 0) {
        setSequenceAccess({
          module: "Work Order Riskflass",
          level: _sequenceAccess[0].level,
          siteIds: _sequenceAccess?.map((item: any) =>
            item?.siteId?.toString()
          ),
        });
      } else {
        setSequenceAccess({
          module: "",
          level: "",
          siteIds: [],
        });
      }
    } catch (error: any) {
      console.log(error.response?.data?.message || "An error occurred");
    }
  };

  const validateForm = () => {
    if (riskflassItems.length === 0) {
      message.error("Please add at least one riskflags item");
      return false;
    }

    // Validate that all items have name
    const itemsWithEmptyName = riskflassItems.filter(
      (item, index) => !item.name || item.name.trim() === ""
    );

    // Validate remarks - mandatory for all items
    const itemsWithEmptyRemarks = riskflassItems.filter(
      (item) => !item.remarks || item.remarks.trim() === ""
    );

    // Validate amount - mandatory only if isChargeable is checked
    const itemsWithChargeableButNoAmount = riskflassItems.filter(
      (item) =>
        item.isChargeable &&
        (!item.amount || item.amount === 0 || item.amount === "")
    );

    // Show single error message if any required fields are missing
    if (itemsWithEmptyName.length > 0) {
      message.error("Please fill name for all items");
      return false;
    }

    if (itemsWithEmptyRemarks.length > 0) {
      message.error("Please fill remarks for all items");
      return false;
    }

    if (itemsWithChargeableButNoAmount.length > 0) {
      message.error(
        "Please enter amount for items where 'Is Chargeable' is checked"
      );
      return false;
    }

    // Validate amount for existing items
    for (const item of riskflassItems) {
      if (item.isExisting) {
        const totalRecovered = item.totalRecoveredAmount || 0;
        const totalPending = item.totalPendingRecoveredAmount || 0;
        const cumulativeRecoveredAmount = totalRecovered + totalPending;
        const amount = item.amount || 0;

        if (amount < cumulativeRecoveredAmount) {
          message.error(
            `Amount for "${
              item.name
            }" cannot be less than ₹${cumulativeRecoveredAmount.toLocaleString("en-IN")} (cumulative recovered amount)`
          );
          return false;
        }
      }
    }

    return true;
  };

  const handleAddRow = () => {
    const newId = Math.max(...riskflassItems.map((item) => item.id), 0) + 1;
    setRiskflassItems([
      ...riskflassItems,
      {
        id: newId,
        name: "",
        isChargeable: false,
        amount: 0,
        remarks: "",
      },
    ]);
  };

  const handleDeleteRow = (id: number) => {
    const itemToDelete = riskflassItems.find((item) => item.id === id);

    // Check if it's an existing item with recovered amount
    if (
      itemToDelete?.isExisting &&
      (itemToDelete?.totalRecoveredAmount || 0) > 0
    ) {
      message.error(
        "Cannot delete item with recovered amount. This item has already been recovered."
      );
      return;
    }

    if (riskflassItems.length === 1) {
      message.warning("At least one row is required");
      return;
    }

    setRiskflassItems(riskflassItems.filter((item) => item.id !== id));
  };

  const handleItemChange = (id: number, field: string, value: any) => {
    setRiskflassItems(
      riskflassItems.map((item) => {
        if (item.id === id) {
          // Clear amount when isChargeable is unchecked
          if (field === "isChargeable" && !value) {
            return { ...item, [field]: value, amount: 0 };
          }
          // Validate amount with debounce for existing items
          if (field === "amount" && item.isExisting) {
            // Clear existing timeout for this item
            if (validationTimeoutRef.current[id]) {
              clearTimeout(validationTimeoutRef.current[id]);
            }

            // Set new timeout to validate after user stops typing (500ms delay)
            validationTimeoutRef.current[id] = setTimeout(() => {
              setRiskflassItems((prevItems) => {
                const currentItem = prevItems.find((i) => i.id === id);
                if (currentItem?.isExisting) {
                  const totalRecovered = currentItem.totalRecoveredAmount || 0;
                  const totalPending =
                    currentItem.totalPendingRecoveredAmount || 0;
                  const cumulativeRecoveredAmount =
                    totalRecovered + totalPending;

                  // Handle all cases: null, undefined, empty string, or numeric value
                  let newAmount = 0;
                  const currentValue = currentItem.amount;
                  if (
                    currentValue === null ||
                    currentValue === undefined ||
                    currentValue === ""
                  ) {
                    newAmount = 0;
                  } else {
                    const parsed = Number(currentValue);
                    newAmount = isNaN(parsed) ? 0 : parsed;
                  }

                  // Check and show error if amount is less than cumulative recovered amount
                  if (
                    cumulativeRecoveredAmount > 0 &&
                    newAmount < cumulativeRecoveredAmount
                  ) {
                    message.error(
                      `Minimum amount is ₹${cumulativeRecoveredAmount.toLocaleString("en-IN")} (cumulative recovered amount)`
                    );
                  }
                }
                return prevItems;
              });
            }, 500);
          }
          return { ...item, [field]: value };
        }
        return item;
      })
    );
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);

      // Find deleted items (items that were in initial load but not in current list)
      const currentItemIds = new Set(
        riskflassItems
          .map((item) => item.workOrderAdvanceDeductionId)
          .filter((id) => id != null)
      );
      const deletedLineIds = Array.from(initialItemIds).filter(
        (id) => !currentItemIds.has(id)
      );

      // Format lines according to API spec
      // Only send new items or existing items that have been modified
      const lines = riskflassItems
        .filter((item) => {
          // Include new items (no workOrderAdvanceDeductionId)
          if (!item.workOrderAdvanceDeductionId) {
            return true;
          }
          // For existing items, check if any field has changed
          const initialItem = initialItems.find(
            (init) =>
              init.workOrderAdvanceDeductionId ===
              item.workOrderAdvanceDeductionId
          );
          if (!initialItem) {
            return true; // Item not found in initial state, include it
          }
          // Compare fields to check if item has been modified
          return (
            initialItem.name !== item.name ||
            initialItem.amount !== item.amount ||
            initialItem.remarks !== item.remarks ||
            initialItem.isChargeable !== item.isChargeable
          );
        })
        .map((item) => {
          const line: any = {
            name: item.name || "",
            isChargable: item.isChargeable,
            totalAmount: item.amount || 0,
            notes: item.remarks || "",
          };
          // Include workOrderAdvanceDeductionId only for existing items
          if (item.workOrderAdvanceDeductionId) {
            line.workOrderAdvanceDeductionId = item.workOrderAdvanceDeductionId;
          }
          return line;
        });

      const requestBody = {
        workOrderId: workOrder.workOrderId,
        type: "Risk Flag",
        lines: lines,
        deletedLineIds: deletedLineIds,
      };

      const response: any = await workOrderApis.submitRiskFlags(requestBody);

      if (response && response.status) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Riskflags submitted successfully",
        }).then(() => {
          const searchParams = new URLSearchParams(location.search);
          const flowType = searchParams.get("flowType");
          const flowTypeQuery = flowType ? `?flowType=${flowType}` : "";
          navigate(`/work-order-view/${id}${flowTypeQuery}`, {
            state: { fromRiskflagsOrAdvance: true },
          });
        });
      } else {
        throw new Error(response?.message || "Failed to submit riskflags");
      }
    } catch (error: any) {
      console.error("Error submitting riskflags:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to submit riskflags. Please try again.";
      message.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle>Loading...</BlockTitle>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>
        <Block>
          <div className="text-center py-5">
            <Spinner color="primary" />
          </div>
        </Block>
      </Content>
    );
  }

  if (!workOrder) {
    return (
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle>Work Order Not Found</BlockTitle>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>
      </Content>
    );
  }

  return (
    <>
      <Head title="Add Riskflags" />
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle page>
                <WarningOutlined className="me-2" />
                Add Riskflags - {workOrder.workOrderNumber}
              </BlockTitle>
              <div className="nk-block-des text-soft">
                <p>Add riskflags details for work order</p>
              </div>
            </BlockHeadContent>
            <BlockHeadContent>
              <Button
                color="light"
                outline
                className="bg-white d-none d-sm-inline-flex"
                onClick={() => {
                  const searchParams = new URLSearchParams(location.search);
                  const flowType = searchParams.get("flowType");
                  const flowTypeQuery = flowType ? `?flowType=${flowType}` : "";
                  navigate(`/work-order-view/${id}${flowTypeQuery}`, {
                    state: { fromRiskflagsOrAdvance: true },
                  });
                }}
              >
                <Icon name="arrow-left"></Icon>
                <span>Back</span>
              </Button>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        <Block>
          <Card className="card-bordered">
            <div className="card-inner">
              <Form form={form} layout="vertical">
                {/* Header Information */}
                <Row
                  className="g-3"
                  style={{
                    marginBottom: 24,
                    padding: 16,
                    background: "#f5f5f5",
                    borderRadius: 8,
                  }}
                >
                  <Col md="3">
                    <div>
                      <strong>WO #:</strong> {workOrder.workOrderNumber}
                    </div>
                  </Col>
                  <Col md="3">
                    <div>
                      <strong>Contractor:</strong> {workOrder.contractorName}
                    </div>
                  </Col>
                  <Col md="3">
                    <div>
                      <strong>Site:</strong> {workOrder.siteName}
                    </div>
                  </Col>
                  <Col md="3">
                    <div>
                      <strong>Total Value:</strong> ₹
                      {workOrder.grossValue?.toLocaleString("en-IN") || "0"}
                    </div>
                  </Col>
                </Row>

                {/* Riskflags Details Table */}
                <Row className="mb-4">
                  <Col md="12">
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: 16,
                      }}
                    >
                      <h6 style={{ margin: 0 }}>Riskflags Items</h6>
                      <AntButton
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={handleAddRow}
                      >
                        Add Row
                      </AntButton>
                    </div>
                    <Table
                      dataSource={riskflassItems}
                      rowKey="id"
                      pagination={false}
                      bordered
                      size="small"
                      columns={[
                        {
                          title: "S.No",
                          key: "sno",
                          width: 80,
                          align: "center",
                          render: (_: any, __: any, index: number) => index + 1,
                        },
                        {
                          title: (
                            <span>
                              Name <span style={{ color: "red" }}>*</span>
                            </span>
                          ),
                          key: "name",
                          width: 250,
                          render: (_: any, record: any) => (
                            <Input
                              value={record.name}
                              onChange={(e) =>
                                handleItemChange(
                                  record.id,
                                  "name",
                                  e.target.value
                                )
                              }
                              placeholder="Enter name"
                            />
                          ),
                        },
                        {
                          title: (
                            <span>
                              Is Chargeable{" "}
                              <span style={{ color: "red" }}>*</span>
                            </span>
                          ),
                          key: "isChargeable",
                          width: 150,
                          align: "center",
                          render: (_: any, record: any) => (
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: "8px",
                              }}
                            >
                              <Checkbox
                                checked={record.isChargeable}
                                onChange={(e) =>
                                  handleItemChange(
                                    record.id,
                                    "isChargeable",
                                    e.target.checked
                                  )
                                }
                              />
                              <span>Yes</span>
                            </div>
                          ),
                        },
                        {
                          title: (
                            <span>
                              Amount{" "}
                              <span style={{ color: "red", fontSize: "12px" }}>
                                * (if chargeable)
                              </span>
                            </span>
                          ),
                          key: "amount",
                          width: 200,
                          render: (_: any, record: any) => (
                            <InputNumber
                              style={{ width: "100%" }}
                              value={record.amount}
                              onChange={(value) =>
                                handleItemChange(record.id, "amount", value)
                              }
                              min={0}
                              precision={2}
                              controls={false}
                              disabled={!record.isChargeable}
                              formatter={indianCurrencyFormatter}
                              parser={indianCurrencyParser}
                              placeholder={
                                record.isChargeable
                                  ? "Enter amount"
                                  : "Check 'Is Chargeable' first"
                              }
                              onKeyPress={(e) => {
                                // Allow: backspace, delete, tab, escape, enter, decimal point, and numbers
                                if (
                                  !/[0-9.]/.test(e.key) &&
                                  ![
                                    "Backspace",
                                    "Delete",
                                    "Tab",
                                    "Escape",
                                    "Enter",
                                  ].includes(e.key)
                                ) {
                                  e.preventDefault();
                                }
                              }}
                            />
                          ),
                        },
                        {
                          title: "Cumulative Recovered Amount",
                          key: "recoveredAmount",
                          width: 250,
                          align: "right",
                          render: (_: any, record: any) => {
                            if (record.isExisting) {
                              const totalRecovered =
                                record.totalRecoveredAmount || 0;
                              const totalPending =
                                record.totalPendingRecoveredAmount || 0;
                              const cumulativeAmount =
                                totalRecovered + totalPending;
                              return (
                                <div style={{ textAlign: "right" }}>
                                  <span style={{ fontWeight: 500 }}>
                                    ₹
                                    {cumulativeAmount.toLocaleString("en-IN", {
                                      maximumFractionDigits: 2,
                                    })}
                                  </span>
                                </div>
                              );
                            }
                            return <span style={{ color: "#999" }}>-</span>;
                          },
                        },
                        {
                          title: (
                            <span>
                              Remarks <span style={{ color: "red" }}>*</span>
                            </span>
                          ),
                          key: "remarks",
                          render: (_: any, record: any) => (
                            <Input
                              value={record.remarks}
                              onChange={(e) =>
                                handleItemChange(
                                  record.id,
                                  "remarks",
                                  e.target.value
                                )
                              }
                              placeholder="Enter remarks"
                            />
                          ),
                        },
                        {
                          title: "Action",
                          key: "action",
                          width: 100,
                          align: "center",
                          render: (_: any, record: any) => (
                            <AntButton
                              type="link"
                              danger
                              icon={<DeleteOutlined />}
                              onClick={() => handleDeleteRow(record.id)}
                              disabled={
                                riskflassItems.length === 1 ||
                                (record.isExisting &&
                                  (record.totalRecoveredAmount || 0) > 0)
                              }
                            />
                          ),
                        },
                      ]}
                    />
                  </Col>
                </Row>

                {/* Action Buttons */}
                <div
                  style={{
                    textAlign: "right",
                    borderTop: "1px solid #f0f0f0",
                    paddingTop: 16,
                    marginTop: 16,
                  }}
                >
                  <Button
                    color="light"
                    onClick={() => {
                      const searchParams = new URLSearchParams(location.search);
                      const flowType = searchParams.get("flowType");
                      const flowTypeQuery = flowType
                        ? `?flowType=${flowType}`
                        : "";
                      navigate(`/work-order-view/${id}${flowTypeQuery}`, {
                        state: { fromRiskflagsOrAdvance: true },
                      });
                    }}
                    style={{ marginRight: 8 }}
                    disabled={submitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    color="primary"
                    onClick={handleSubmit}
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>
                        <Spinner size="sm" /> Submitting...
                      </>
                    ) : (
                      <>Submit</>
                    )}
                  </Button>
                </div>
              </Form>
            </div>
          </Card>
        </Block>
      </Content>
    </>
  );
};

export default WorkOrderRiskflags;
